import { AssetClass } from '@/entities/assets'
import { DocumentClass } from '@/entities/documents'
import { TagClass } from '@/entities/tags'
import { SearchSummary } from './types'
import { Transaction, TransactionSettings } from '@types'

import { DOCUMENT_FIELD } from '@/entities/documents/utils/const'
import { ASSET_FIELD } from '@/entities/assets/utils/const'

import { stringToLocalDateString } from '@/helpers/dates'
import { getDocumentTagName } from '@/entities/assets/components/utils/helpers'
import { getTransactionString } from '@/views/Data/Transactions/components/utils/helpers'
import { mapTags } from '@/store/analytics/utils/helpers'

export const filterFunction = (item: SearchSummary, string: string) =>
  item.result.toLowerCase().includes(string.toLowerCase())

export const prepareTagsSearchList =
  (name: string, routeName: string) =>
    ([, instance]: [string, TagClass]): SearchSummary => ({
      id: instance.id,
      result: instance.displayPair,
      type: `${name} / ${instance.displayPair}`,
      title: instance.displayPair,
      category: name,
      route: { name: routeName, params: { id: instance.id } },
    })

export const prepareAssetsSearchList =
  (name: string, routeName: string) =>
    ([, instance]: [string, AssetClass]): SearchSummary => {
      const searchData = [
        ...new Set([
          instance.field(ASSET_FIELD.NAME).value,
          instance.field(ASSET_FIELD.TICKER).value,
          instance.field(ASSET_FIELD.TYPE).value,
          instance.field(ASSET_FIELD.CURRENCY).value,
        ]),
      ]
      return {
        id: instance.id,
        result: searchData.join(', '),
        type: `${name} / ${instance.field(ASSET_FIELD.NAME).value}`,
        title: instance.field<string>(ASSET_FIELD.NAME).value,
        category: name,
        route: { name: routeName, params: { id: instance.id } },
      }
    }

export const prepareAccountsSearchList =
  (name: string, routeName: string, tagsBunch: Map<string, TagClass>) =>
    ([, instance]: [string, AssetClass]): SearchSummary => {
      const tags = mapTags(
        instance.field<string[]>(ASSET_FIELD.TAGS).value || [],
        tagsBunch,
      )
      const searchData = [
        ...new Set([
          instance.field(ASSET_FIELD.NAME).value,
          tags.Category,
          tags.Subcategory,
        ]),
      ]
      return {
        id: instance.id,
        result: searchData.join(', '),
        type: `${name} / ${instance.field(ASSET_FIELD.NAME).value}`,
        title: instance.field<string>(ASSET_FIELD.NAME).value,
        category: name,
        route: {
          name: routeName,
          params: { id: instance.id },
        },
      }
    }

export const prepareDocumentsSearchList =
  (name: string, routeName: string, assetsBunch: Map<string, AssetClass>) =>
    ([, instance]: [string, DocumentClass]): SearchSummary => {
      const assetId = instance.field<string>(DOCUMENT_FIELD.ASSET_ID).value
      const assetInstance = assetsBunch.get(assetId)
      const searchData = [
        ...new Set([
          instance.field(DOCUMENT_FIELD.NAME).value,
          instance.field(DOCUMENT_FIELD.PATH).value,
          assetInstance?.field(ASSET_FIELD.NAME).value,
          instance
            .field<string[]>(DOCUMENT_FIELD.TAGS)
            .value.map(tag => getDocumentTagName(tag)),
        ]),
      ]
      return {
        id: instance.id,
        result: searchData.join(', '),
        type: `${name} / ${instance.field(DOCUMENT_FIELD.NAME).value}`,
        title: instance.field<string>(DOCUMENT_FIELD.NAME).value,
        category: name,
        route: { name: routeName, params: { id: instance.id } },
      }
    }

export const prepareTransactionsSearchList =
  (
    name: string,
    routeName: string,
    settings: TransactionSettings[],
    getAssetName: (id: string) => string | undefined,
  ) =>
    (item: Transaction): SearchSummary => {
      const formattedDate = stringToLocalDateString(item.date)
      const str = getTransactionString(item, settings.find(setting => setting.name === item.type), getAssetName)
      const searchData = [
        ...new Set([
          ...Object.values(str || {})
            .flat()
            .map(item => item.value),
          ...(item.description ? [item.description] : []),
          item.type,
          formattedDate,
        ]),
      ]
      return {
        id: item.id,
        result: searchData.join(', '),
        type: `${name} / ${item.type}`,
        additional: formattedDate,
        title: item.description || item.type,
        category: name,
        route: {
          name: routeName,
          params: { id: item.id },
        },
      }
    }
